import { getPricingPlan } from '@common/locales';
import { YEARLY_PRICE_MULTIPLIER } from '@common/schemas/subscription-plan.schema';
import { Warning as AlertTriangle, Event as Calendar, Check, CreditCard } from '@mui/icons-material';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CancelSubscriptionDialog } from './CancelSubscriptionDialog';
import { FreeSubscriptionView } from './FreeSubscriptionView';
import { PaymentHistory } from './PaymentHistory';

import type { UserSubscription } from '@/types/subscription';

import { useFirebase } from '@/firebase';
import { useToast } from '@/hooks/use-toast';
import { useSubscription } from '@/hooks/useSubscription';
import { formatPrice, toSafeDate } from '@/lib/subscription-utils';

export function SubscriptionInfo() {
  const { backend } = useFirebase();
  const { t } = useTranslation();
  const { toast } = useToast();
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [reactivating, setReactivating] = useState(false);

  const {
    user: dbUser,
    maxTokens,
    tokensSpent,
    tokensRemaining,
  } = useSubscription();
  if (!dbUser) return null;
  const { subscription: UserSubscriptions } = dbUser;

  const isPaidPlan = dbUser.planId && dbUser.planId !== 'free';

  const handleCancelClick = () => {
    setCancelDialogOpen(true);
  };

  const handleReactivate = async () => {
    if (!backend || reactivating) return;
    setReactivating(true);
    try {
      await backend.billing.reactivateSubscription();
      toast({
        title: t('profile.reactivateSuccessTitle'),
        description: canKeepCurrentPlan
          ? t('profile.keepCurrentSuccessDesc')
          : t('profile.reactivateSuccessDesc'),
      });
    } catch (error) {
      console.error('Error reactivating subscription:', error);
      toast({
        title: t('profile.reactivateErrorTitle'),
        description: t('profile.reactivateErrorDesc'),
        variant: 'destructive',
      });
    } finally {
      setReactivating(false);
    }
  };

  const handleManageBilling = async () => {
    if (!backend) return;
    try {
      const url = await backend.billing.createPortalSession(window.location.href);
      window.location.href = url;
    } catch (err) {
      console.error('Portal error:', err);
    }
  };

  const planId = dbUser.planId || 'free';
  const staticPlan = getPricingPlan(planId);
  const benefits = t(`pricing.plans.${planId}.features`, { returnObjects: true });
  const benefitsList = Array.isArray(benefits) ? benefits : [];

  // Free tier user view delegated to helper component
  if (!isPaidPlan) {
    return (
      <FreeSubscriptionView
        dbUser={dbUser}
        benefitsList={benefitsList}
        handleManageBilling={handleManageBilling}
      />
    );
  }

  const subscription: UserSubscription = UserSubscriptions?.current || {
    planId: dbUser.planId || 'free',
    planName: dbUser.planName || (dbUser.planId ? dbUser.planId.charAt(0).toUpperCase() + dbUser.planId.slice(1) : 'Free'),
    status: 'active',
    startDate: new Date().toISOString(),
    currentPeriodEnd: new Date().toISOString(),
    cancelAtPeriodEnd: false,
    maxTokens: 0,
    updatedAt: new Date().toISOString(),
  };

  // Paid subscription
  const hasScheduledCancellation =
    subscription.cancelAtPeriodEnd || Boolean(subscription.cancelAt);
  const isCancelled = subscription.status === 'cancelled' || hasScheduledCancellation;
  const nextBillingDate = toSafeDate(subscription.currentPeriodEnd);
  const cancellationDate = toSafeDate(subscription.cancelAt) ?? nextBillingDate;
  const pendingPlanDate =
    toSafeDate(subscription.pendingPlanEffectiveAt) ?? nextBillingDate;
  const canReactivate =
    Boolean(dbUser.stripeCustomerId) &&
    hasScheduledCancellation &&
    subscription.status !== 'cancelled' &&
    subscription.status !== 'expired';
  const canKeepCurrentPlan =
    Boolean(dbUser.stripeCustomerId) &&
    !isCancelled &&
    Boolean(subscription.pendingPlanId);
  const billingInterval = subscription.billingInterval ?? 'month';
  const priceMultiplier = billingInterval === 'year' ? YEARLY_PRICE_MULTIPLIER : 1;
  const billingPeriodLabel = billingInterval === 'year'
    ? t('pricing.perYear')
    : t('pricing.perMonth');

  return (
    <>
      <Paper sx={{ p: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5" fontWeight={600}>
            {t('profile.title')}
          </Typography>
          <Chip
            label={isCancelled ? t('profile.cancelled') : t('profile.active')}
            color={isCancelled ? 'warning' : 'success'}
            size="small"
          />
        </Box>

        <Divider sx={{ my: 2 }} />

        {isCancelled && cancellationDate && (
          <Alert severity="warning" icon={<AlertTriangle sx={{ fontSize: 20 }} />} sx={{ mb: 3 }}>
            {t('profile.cancelWarning', {
              date: cancellationDate.toLocaleDateString(undefined, {
                month: 'long',
                day: 'numeric',
                year: 'numeric',
              }),
            })}
          </Alert>
        )}

        {!isCancelled && subscription.pendingPlanId && pendingPlanDate && (
          <Alert severity="info" icon={<AlertTriangle sx={{ fontSize: 20 }} />} sx={{ mb: 3 }}>
            {t('profile.pendingDowngrade', {
              planName: t(`pricing.plans.${subscription.pendingPlanId}.name`),
              date: pendingPlanDate ? pendingPlanDate.toLocaleDateString(undefined, {
                month: 'long',
                day: 'numeric',
                year: 'numeric',
              }) : '',
            })}
          </Alert>
        )}

        {/* Plan Details */}
        <Box sx={{ my: 3 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {t('profile.currentPlan')}
          </Typography>
          <Typography variant="h4" fontWeight={700} color="primary.main" gutterBottom>
            {t(`pricing.plans.${planId}.name`)}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CreditCard sx={{ fontSize: 16 }} />
              <Typography variant="body2" color="text.secondary">
                {formatPrice(
                  (staticPlan?.price ?? 0) * priceMultiplier,
                  staticPlan?.currency,
                )} / {billingPeriodLabel}
              </Typography>
            </Box>
            {nextBillingDate && !isCancelled && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Calendar sx={{ fontSize: 16 }} />
                <Typography variant="body2" color="text.secondary">
                  {t('profile.nextBilling', {
                    date: nextBillingDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                  })}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        {maxTokens > 0 && (
          <>
            <Divider sx={{ my: 3 }} />
            <Box
              sx={{
                p: 3,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'action.hover',
              }}
            >
              <Typography variant="h6" fontWeight={600}>
                {t('profile.aiTokensTitle')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
                {t('profile.aiTokensDescription')}
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
                  gap: 2,
                }}
              >
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {t('profile.tokensRemaining')}
                  </Typography>
                  <Typography variant="h5" color="primary.main" fontWeight={700}>
                    {tokensRemaining.toLocaleString()}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {t('profile.tokensUsed')}
                  </Typography>
                  <Typography variant="h5" fontWeight={700}>
                    {tokensSpent.toLocaleString()}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {t('profile.tokenAllowance')}
                  </Typography>
                  <Typography variant="h5" fontWeight={700}>
                    {maxTokens.toLocaleString()}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </>
        )}

        <Divider sx={{ my: 3 }} />

        {/* Features */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom fontWeight={600}>
            {t('profile.includedFeatures')}
          </Typography>
          {benefitsList.length > 0 ? (
            <List dense>
              {benefitsList.map((feature: string, idx: number) => (
                <ListItem key={idx} sx={{ px: 0 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <Check sx={{ fontSize: 18, color: 'success.main' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={feature}
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {t('common.none')}
            </Typography>
          )}
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
          <Button
            component={Link}
            to="/pricing"
            variant="contained"
            fullWidth
          >
            {t('profile.changePlan')}
          </Button>

          {dbUser?.stripeCustomerId && (
            <Button
              variant="contained"
              onClick={handleManageBilling}
              fullWidth
              startIcon={<CreditCard />}
              color="warning"
            >
              {t('profile.manageBilling')}
            </Button>
          )}

          {!isCancelled && !dbUser?.stripeCustomerId && (
            <Button
              variant="outlined"
              color="error"
              onClick={handleCancelClick}
              fullWidth
            >
              {t('profile.cancelSubscription')}
            </Button>
          )}
          {(canReactivate || canKeepCurrentPlan) && (
            <Button
              variant="outlined"
              onClick={handleReactivate}
              disabled={reactivating}
              fullWidth
            >
              {reactivating
                ? t('common.loading')
                : canKeepCurrentPlan
                  ? t('profile.keepCurrentPlan')
                  : t('profile.reactivateSubscription')}
            </Button>
          )}
        </Box>
      </Paper>

      <CancelSubscriptionDialog
        open={cancelDialogOpen}
        onClose={() => setCancelDialogOpen(false)}
        subscription={subscription}
      />
      {dbUser.stripeCustomerId && <PaymentHistory />}
    </>
  );
}
